// Mirrors backend utils/company_identity.py — "is this the SAME company?"
// Shared city / industry words never identify a company: "Shanghai Junshi
// Biosciences" ≠ "Shanghai Henlius" (Sep 2026 Junshi incident).
import { areSamePharma } from './pharmaAliases';

const GENERIC = new Set([
  'bioscience', 'biosciences', 'biosciencs', 'biotech', 'biotechnology', 'biotechnologies',
  'biopharmaceutical', 'biopharmaceuticals', 'biopharma', 'biopharm', 'biotherapeutics',
  'therapeutics', 'therapeutic', 'biologics', 'biologic', 'biomedical', 'biomed', 'medicine',
  'medicines', 'medical', 'pharmaceutical', 'pharmaceuticals', 'pharma', 'oncology', 'oncologic',
  'technology', 'technologies', 'tech', 'lifescience', 'lifesciences', 'life', 'science',
  'sciences', 'scientific', 'healthcare', 'health', 'diagnostic', 'diagnostics', 'research',
  'discovery', 'solutions', 'innovations', 'innovation', 'global', 'international', 'industries',
  'industrial', 'company', 'limited', 'holdings', 'holding', 'ventures', 'partners', 'capital',
  'labs', 'laboratory', 'laboratories', 'asia', 'china', 'europe', 'japan', 'korea', 'india',
  'shanghai', 'beijing', 'guangzhou', 'suzhou', 'tianjin', 'hangzhou', 'chengdu', 'tokyo', 'seoul',
  'boston', 'cambridge', 'incorporated', 'jiangsu', 'guangdong', 'shenzhen', 'sichuan', 'zhejiang',
  'shandong', 'fujian', 'anhui', 'jiangxi', 'hubei', 'hunan', 'liaoning', 'wuhan', 'xiamen',
  'nanjing', 'shenyang', 'qingdao', 'dalian', 'kunming', 'xian', 'kong', 'hong', 'taiwan', 'mumbai',
  'hyderabad', 'bangalore', 'ahmedabad', 'university', 'universities', 'institute', 'institutes',
  'college', 'school', 'academy', 'hospital', 'clinic', 'center', 'centre', 'national', 'regional',
  'metropolitan', 'general', 'advanced', 'novel', 'platform', 'platforms', 'system', 'systems',
  'group', 'groups', 'corporation', 'corporations', 'products', 'private', 'public', 'inc', 'ltd',
  'corp', 'gmbh', 'plc', 'llc', 'co', 'ag', 'sa', 'usa', 'emea', 'apac',
]);

const EXTRA_GENERIC = ['pharm', 'pharma', 'pharmaceutica', 'pharmaceutics', 'bio', 'biol', 'med', 'medi', 'medic', 'lab', 'labs', 'sci', 'tech', 'thera', 'health', 'care', 'life', 'gene', 'cell', 'chem', 'chemical', 'chemicals', 'drug', 'drugs', 'trading', 'marketing', 'distribution', 'services', 'service', 'enterprise', 'enterprises', 'kgaa', 'ltda', 'pty', 'bhd', 'sdn', 'spa', 'srl'];
EXTRA_GENERIC.forEach((g) => GENERIC.add(g));
// Structural suffixes only (pharma / group / bio…) — allowed as the leftover
// of a prefix match ("junshi" ↔ "junshipharma"). Mirrors backend _SUFFIX_GENERIC.
const SUFFIX_GENERIC = new Set(GENERIC);
// Sector / geography words shared by unrelated companies. Mirrors backend
// _COMMON_WORDS (Sep 2026: "Faes Farma" ≠ "Kalbe Farma", "Salvat Laboratorios"
// ≠ "Laboratorios Carnot" — the frontend index leaked their contacts).
const COMMON_WORDS = new Set([
  'vaccine', 'vaccin', 'vaccines', 'immune', 'immuno', 'immunology', 'immunotherapy', 'immunotherapeutics',
  'alpha', 'beta', 'gamma', 'delta', 'omega', 'sigma', 'cure', 'micro', 'nano', 'stem', 'rna', 'dna',
  'peptide', 'antibody', 'antibodies', 'protein', 'molecular', 'molecule', 'precision', 'genomics', 'genomic',
  'cellular', 'tissue', 'vector', 'viral', 'virus', 'microbiome', 'plasma', 'blood', 'vision', 'surgical',
  'device', 'devices', 'digital', 'data', 'animal', 'consumer', 'generic', 'generics', 'specialty',
  'specialities', 'clinical', 'wellness', 'nutrition', 'dental', 'derma', 'skin', 'eye',
  'heart', 'brain', 'neuro', 'cardio', 'renal', 'kidney', 'liver', 'lung', 'onco', 'translational',
  'laboratorio', 'laboratorios', 'laboratoire', 'laboratoires', 'laboratorium', 'laboratori', 'labor',
  'farma', 'farmaceutica', 'farmaceutici', 'farmaceuticos', 'farmaceuticas', 'pharmaceutique', 'pharmazeutische',
  'medica', 'medico', 'medicos', 'quimica', 'quimico', 'quimicos', 'grupo', 'groupe', 'gruppo',
  'institut', 'instituto', 'istituto', 'fondazione', 'foundation', 'royal', 'united', 'standard', 'central',
  'western', 'eastern', 'northern', 'southern', 'north', 'south', 'east', 'west', 'pacific', 'atlantic',
  'asian', 'chinese', 'japanese', 'korean', 'indian', 'america', 'american', 'americas', 'european',
  'australia', 'australian', 'canada', 'canadian', 'swiss', 'german', 'deutsche', 'french', 'british',
  'italia', 'italian', 'espana', 'latam', 'african', 'africa',
]);
COMMON_WORDS.forEach((g) => GENERIC.add(g));
const STRUCTURAL_LIST = [...SUFFIX_GENERIC].filter((g) => g.length >= 4);
// "pharm" is a prefix of "pharma"; "pharmaceutica" starts with "pharma" — both generic.
// Common sector words match exactly (or their plural), never by prefix.
const isGeneric = (t) => GENERIC.has(t) || /^\d+$/.test(t)
  || (t.endsWith('s') && COMMON_WORDS.has(t.slice(0, -1)))
  || STRUCTURAL_LIST.some((g) => g.startsWith(t) || t.startsWith(g));

const _tokCache = new Map();
export const brandTokens = (name) => {
  const key = String(name || '');
  let v = _tokCache.get(key);
  if (v) return v;
  v = new Set(
    key
      .toLowerCase()
      .replace(/&/g, 'and')
      .split(/[\s\-_/,.()&]+/)
      .filter((t) => t.length >= 4 && !isGeneric(t)),
  );
  if (_tokCache.size > 20000) _tokCache.clear();
  _tokCache.set(key, v);
  return v;
};

// '' / 's' / 'pharma' / 'pharmagroup' → true; 'ine' / 'f' → false.
const genericSuffix = (rest, depth = 0) => {
  if (rest === '' || rest === 's') return true;
  if (SUFFIX_GENERIC.has(rest) || (rest.endsWith('s') && SUFFIX_GENERIC.has(rest.slice(0, -1)))) return true;
  if (depth >= 1) return false;
  return [...SUFFIX_GENERIC].some((g) => g.length >= 3 && rest.startsWith(g) && genericSuffix(rest.slice(g.length), depth + 1));
};

// Same brand token. Prefix-tolerant ONLY when the leftover is a structural
// suffix ("junshi" ↔ "junshipharma"), never "august" ↔ "augustine".
const tokMatch = (a, b) => {
  if (a === b) return true;
  const [longer, shorter] = a.length >= b.length ? [a, b] : [b, a];
  return longer.startsWith(shorter) && genericSuffix(longer.slice(shorter.length));
};

export const sharesBrand = (nameA, nameB) => {
  const ta = [...brandTokens(nameA)];
  const tb = [...brandTokens(nameB)];
  return ta.some((x) => tb.some((y) => tokMatch(x, y)));
};

const KGAA_RE = /kgaa|darmstadt|emd\b|serono|merck\s*group|merckgroup|merck\s*healthcare/i;
const MSD_RE = /\bmsd\b|sharp|dohme|merck\s*(&|and)\s*co|rahway|merck\.com/i;
export const merckFamily = (name) => {
  if (!/merck|\bmsd\b|\bemd\b|serono/i.test(name)) return null;
  const k = KGAA_RE.test(name);
  const m = MSD_RE.test(name);
  if (k && !m) return 'KGAA';
  if (m && !k) return 'MSD';
  return 'UNRESOLVED';
};

export const sameCompany = (nameA, nameB) => {
  if (!nameA || !nameB) return false;
  const fa = merckFamily(nameA);
  const fb = merckFamily(nameB);
  if (fa && fb && fa !== fb && fa !== 'UNRESOLVED' && fb !== 'UNRESOLVED') return false;
  if (areSamePharma(nameA, nameB)) return true;
  return sharesBrand(nameA, nameB);
};

const merckEmailFamily = (email) => {
  const host = String(email || '').split('@')[1]?.toLowerCase() || '';
  if (/(^|\.)(merckgroup\.com|emdserono\.com|emdgroup\.com)$/.test(host)) return 'KGAA';
  if (/(^|\.)(merck\.com|msd\.com)$/.test(host)) return 'MSD';
  return null;
};

// Drop contacts that provably belong to the OTHER Merck (by email domain
// first, then employer-of-record) when the target is a resolved Merck entity.
export const filterMerckFamily = (targetName, contacts) => {
  const tf = merckFamily(targetName);
  if (!tf || tf === 'UNRESOLVED') return contacts;
  return (contacts || []).filter((c) => {
    const cf = merckEmailFamily(c.email) || merckFamily(c.company_name || '');
    return !cf || cf === 'UNRESOLVED' || cf === tf;
  });
};
